import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import QRCode from 'react-qr-code';
import type { InvoiceHeader, InvoiceDetails, OtherCharges } from '../services/invoiceApi';
import { Branch } from '../services/branchApi';
import { Customer } from '../services/customerApi';
import { formatDate } from '../utils/dateUtils';
import { InvoiceEwayBillLayout } from './InvoiceEwayBillLayout';

function amountInWords(num: number): string {
  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
  ];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const convertHundreds = (n: number): string => {
    let result = '';
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    }
    if (n > 0) result += ones[n] + ' ';
    return result.trim();
  };
  if (num === 0) return 'Zero';
  const whole = Math.floor(num);
  const paise = Math.round((num % 1) * 100);
  const numStr = whole.toString();
  let result = '';
  if (numStr.length > 7) {
    const crores = parseInt(numStr.slice(0, numStr.length - 7), 10);
    if (crores > 0) result += convertHundreds(crores) + ' Crore ';
  }
  if (numStr.length > 5) {
    const lakhs = parseInt(numStr.slice(Math.max(0, numStr.length - 7), numStr.length - 5), 10);
    if (lakhs > 0) result += convertHundreds(lakhs) + ' Lakh ';
  }
  if (numStr.length > 3) {
    const thousands = parseInt(numStr.slice(Math.max(0, numStr.length - 5), numStr.length - 3), 10);
    if (thousands > 0) result += convertHundreds(thousands) + ' Thousand ';
  }
  const hundreds = parseInt(numStr.slice(Math.max(0, numStr.length - 3)), 10);
  if (hundreds > 0) result += convertHundreds(hundreds);
  result = result.trim() + ' Rupees';
  if (paise > 0) result += ' and ' + convertHundreds(paise) + ' Paise';
  return result.trim();
}

interface HsnWiseRow {
  hsn: string;
  taxableValue: number;
  cgstRate: number;
  cgstAmount: number;
  sgstRate: number;
  sgstAmount: number;
  igstRate: number;
  igstAmount: number;
  totalTaxAmount: number;
}

function toDisplayRate(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value > 100 ? value / 100 : value;
}

export function chunkInvoiceLineItems(
  details: InvoiceDetails[],
  firstPageRows: number,
  continuationRows: number
): InvoiceDetails[][] {
  if (details.length === 0) return [[]];
  if (details.length <= firstPageRows) return [details];
  const chunks: InvoiceDetails[][] = [details.slice(0, firstPageRows)];
  let rest = details.slice(firstPageRows);
  while (rest.length > 0) {
    chunks.push(rest.slice(0, continuationRows));
    rest = rest.slice(continuationRows);
  }
  return chunks;
}

/** Body rows per sheet; continuation rows slightly lower when statutory block repeats every page. */
export const CHUNK_FIRST_PAGE_ROWS = 8;
export const CHUNK_CONTINUATION_ROWS = 6;

interface InvoiceChunkedPrintProps {
  invoice: InvoiceHeader;
  branchDetails: Branch | null;
  shipToCustomer: Customer | null;
  billToCustomer: Customer | null;
  details: InvoiceDetails[];
  otherChargesList: OtherCharges[];
  calc: {
    taxableAmount: number;
    totalCgst: number;
    totalSgst: number;
    totalIgst: number;
    totalTax: number;
    totalQty: number;
    totalAmount: number;
  };
  hsnWiseTaxDetails: HsnWiseRow[];
  isGst: boolean;
  qrValue: string;
  copies: number;
  showEwayBillOnView: boolean;
  /** Resolved signer display name for e-invoice digital signature block */
  digitallySignedByName?: string;
}

/**
 * E-invoice document: Header (everything above line items, including IRN/QR) on every page; body =
 * line items split across pages; footer (amount in words + HSN + declaration + doc footer) only on
 * the last body page of each copy. Totals rows in the line table appear only on the last body page.
 */
export const InvoiceChunkedPrint: React.FC<InvoiceChunkedPrintProps> = ({
  invoice,
  branchDetails,
  shipToCustomer,
  billToCustomer,
  details,
  otherChargesList,
  calc,
  hsnWiseTaxDetails,
  isGst,
  qrValue,
  copies,
  showEwayBillOnView,
  digitallySignedByName,
}) => {
  const branchDisplayName = branchDetails?.branchName ?? invoice.branchName ?? '';
  const chunks = chunkInvoiceLineItems(details, CHUNK_FIRST_PAGE_ROWS, CHUNK_CONTINUATION_ROWS);
  const invoicePagesPerCopy = Math.max(1, chunks.length);
  const totalBodyPages = copies * invoicePagesPerCopy;
  /** "Page x of y" on the Tax Invoice is only for e-invoice body sheets (excludes e-way annex). */
  const einvoicePageTotal = totalBodyPages;
  // Hide "(Page x)" in the title for e-invoice prints (matches mockup / requested behavior)
  const showPageNumberInTitle = false;

  const renderAddressAndMeta = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 print:grid-cols-2 gap-6 mb-6 min-w-0">
      <div className="space-y-4 border rounded p-4 min-w-0">
        <div className="border-b pb-3">
          <div className="flex gap-4">
            {branchDetails && (
              <>
                <div className="flex-shrink-0">
                  <img src="/customer-logo/sg-group-logo.jpg" alt="SG Group" className="h-14 w-20 object-contain" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{branchDetails.branchName}</p>
                  <p className="text-xs text-gray-600 leading-snug">{branchDetails.address}</p>
                  <p className="text-xs text-gray-600 leading-snug">GSTIN/UIN: {branchDetails.gstin || 'NA'}</p>
                  <p className="text-xs text-gray-600 leading-snug">
                    State Name: {branchDetails.stateName || 'NA'}, Code: {branchDetails.stateCode || 'NA'}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="border-b pb-3">
          <p className="font-semibold mb-2">Consignee (Ship to):</p>
          {shipToCustomer ? (
            <div>
              <p className="text-sm font-medium text-gray-900">{shipToCustomer.customerName}</p>
              <p className="text-xs text-gray-600 leading-snug mt-0.5">{shipToCustomer.address}</p>
              <p className="text-xs text-gray-600 leading-snug">Vendor Code: {shipToCustomer.vendorCode ?? 'NA'}</p>
              <p className="text-xs text-gray-600 leading-snug">GSTIN/UIN: {shipToCustomer.gstNo ?? 'NA'}</p>
              <p className="text-xs text-gray-600 leading-snug">
                State Name: {shipToCustomer.stateName ?? 'NA'}, Code: {shipToCustomer.stateCode ?? 'NA'}
              </p>
            </div>
          ) : (
            <p className="text-xs text-gray-500">—</p>
          )}
        </div>
        <div>
          <p className="font-semibold mb-2">Buyer (Bill to):</p>
          {billToCustomer ? (
            <div>
              <p className="text-sm font-medium text-gray-900">{billToCustomer.customerName}</p>
              <p className="text-xs text-gray-600 leading-snug mt-0.5">{billToCustomer.address}</p>
              <p className="text-xs text-gray-600 leading-snug">Vendor Code: {billToCustomer.vendorCode ?? 'NA'}</p>
              <p className="text-xs text-gray-600 leading-snug">GSTIN/UIN: {billToCustomer.gstNo ?? 'NA'}</p>
              <p className="text-xs text-gray-600 leading-snug">
                State Name: {billToCustomer.stateName ?? 'NA'}, Code: {billToCustomer.stateCode ?? 'NA'}
              </p>
            </div>
          ) : (
            <p className="text-xs text-gray-500">—</p>
          )}
        </div>
      </div>
      <div className="invoice-meta-lines min-w-0 border border-gray-300 rounded overflow-hidden text-[11px] leading-snug divide-y divide-gray-300">
        <div className="px-2 py-1.5 break-words bg-white">
          <span className="font-normal text-gray-900">Invoice No.: </span>
          <span className="font-bold text-gray-900">{invoice.invoiceNo ?? ''}</span>
        </div>
        <div className="px-2 py-1.5 break-words bg-white">
          <span className="font-normal text-gray-900">Dated: </span>
          <span className="font-bold text-gray-900">{invoice.invoiceDate ? formatDate(invoice.invoiceDate) : ''}</span>
        </div>
        <div className="px-2 py-1.5 break-words bg-white">
          <span className="font-normal text-gray-900">e-Way Bill No.: </span>
          <span className="font-bold text-gray-900">{invoice.ewayBillNo ?? ''}</span>
        </div>
        <div className="px-2 py-1.5 break-words bg-white">
          <span className="font-normal text-gray-900">Delivery Note: </span>
          <span className="font-bold text-gray-900">{invoice.deliverNote ?? ''}</span>
        </div>
        <div className="px-2 py-1.5 break-words bg-white">
          <span className="font-normal text-gray-900">Reference No. &amp; Date: </span>
          <span className="font-bold text-gray-900">{invoice.referenceNoAndDate ?? ''}</span>
        </div>
        <div className="px-2 py-1.5 break-words bg-white">
          <span className="font-normal text-gray-900">Other References: </span>
          <span className="font-bold text-gray-900">{invoice.otherReferences ?? ''}</span>
        </div>
        <div className="px-2 py-1.5 break-words bg-white">
          <span className="font-normal text-gray-900">Buyer&apos;s Order No.: </span>
          <span className="font-bold text-gray-900">{invoice.buyerOrderNo ?? ''}</span>
        </div>
        <div className="px-2 py-1.5 break-words bg-white">
          <span className="font-normal text-gray-900">Buyer order dated: </span>
          <span className="font-bold text-gray-900">{invoice.buyerOrderDate ? formatDate(invoice.buyerOrderDate) : ''}</span>
        </div>
        <div className="px-2 py-1.5 break-words bg-white">
          <span className="font-normal text-gray-900">Dispatch Doc No.: </span>
          <span className="font-bold text-gray-900">{invoice.dispatchDocNo ?? ''}</span>
        </div>
        <div className="px-2 py-1.5 break-words bg-white">
          <span className="font-normal text-gray-900">Delivery Note Date: </span>
          <span className="font-bold text-gray-900">{invoice.deliverNoteDate ? formatDate(invoice.deliverNoteDate) : ''}</span>
        </div>
        <div className="px-2 py-1.5 break-words bg-white">
          <span className="font-normal text-gray-900">Dispatched through: </span>
          <span className="font-bold text-gray-900">{invoice.dispatchedThrough ?? ''}</span>
        </div>
        <div className="px-2 py-1.5 break-words bg-white">
          <span className="font-normal text-gray-900">Destination: </span>
          <span className="font-bold text-gray-900">{invoice.destination ?? ''}</span>
        </div>
        <div className="px-2 py-1.5 break-words bg-white">
          <span className="font-normal text-gray-900">Bill of Lading / LR-RR No.: </span>
          <span className="font-bold text-gray-900">{invoice.billOfLanding ?? ''}</span>
        </div>
        <div className="px-2 py-1.5 break-words bg-white">
          <span className="font-normal text-gray-900">Motor Vehicle No.: </span>
          <span className="font-bold text-gray-900">{invoice.motorVehicleNo ?? ''}</span>
        </div>
        <div className="px-2 py-1.5 break-words bg-white">
          <span className="font-normal text-gray-900">Term of delivery: </span>
          <span className="font-bold text-gray-900">{invoice.termOfDelivery ?? ''}</span>
        </div>
      </div>
    </div>
  );

  const renderLineTableHead = () => (
    <thead>
      <tr className="bg-gray-100">
        <th className="border border-gray-300 px-1 py-1 w-[2rem]">#</th>
        <th className="border border-gray-300 px-1 py-1 w-[30%] min-w-0">Description of Goods</th>
        <th className="border border-gray-300 px-1 py-1 w-[3rem]">Boxes</th>
        <th className="border border-gray-300 px-1 py-1 w-[4.5rem] max-w-[4.5rem]">Heat No/Container</th>
        <th className="border border-gray-300 px-1 py-1 w-[3.5rem]">HSN</th>
        <th className="border border-gray-300 px-1 py-1 w-[2.75rem]">Qty</th>
        <th className="border border-gray-300 px-1 py-1 w-[2.75rem]">Rate</th>
        <th className="border border-gray-300 px-1 py-1 w-[2.25rem]">Per</th>
        <th className="border border-gray-300 px-1 py-1 w-[3.25rem]">Amt</th>
      </tr>
    </thead>
  );

  /** Jurisdiction + e-invoice page x of y (body pages only; e-way sheet is not counted). */
  const renderPrintFooter = (pageIndex: number) => (
    <div className="invoice-print-doc-footer mt-4 pt-2 border-t border-gray-300 text-center text-[11px] leading-snug text-gray-600 space-y-0.5">
      <p className="font-medium">SUBJECT TO JAIPUR JURISDICTION</p>
      <p>This is a Computer Generated Invoice</p>
    </div>
  );
  const renderTotalsAndAfter = () => (
    <>
      <div className="mt-3 border-t pt-3">
        <p className="text-xs text-gray-800 leading-snug">
          <strong>Amount Chargeable (in words):</strong> INR {amountInWords(calc.totalAmount)}
        </p>
      </div>
      <div className="mt-3 border rounded overflow-hidden">
        <table className="w-full border-collapse text-[11px] leading-tight">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">HSN</th>
              <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">Taxable Value</th>
              {!isGst && (
                <>
                  <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">CGST Rate</th>
                  <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">CGST Amt</th>
                  <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">SGST Rate</th>
                  <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">SGST Amt</th>
                </>
              )}
              {isGst && (
                <>
                  <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">IGST Rate</th>
                  <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">IGST Amt</th>
                </>
              )}
              <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">Total Tax Amount</th>
            </tr>
          </thead>
          <tbody>
            {hsnWiseTaxDetails.map((row, idx) => (
              <tr key={idx} className="border-b">
                <td className="border border-gray-300 px-1.5 py-1">{row.hsn}</td>
                <td className="border border-gray-300 px-1.5 py-1">{row.taxableValue.toFixed(2)}</td>
                {!isGst && (
                  <>
                    <td className="border border-gray-300 px-1.5 py-1 text-center">{toDisplayRate(row.cgstRate).toFixed(2)}%</td>
                    <td className="border border-gray-300 px-1.5 py-1 text-right">{row.cgstAmount.toFixed(2)}</td>
                    <td className="border border-gray-300 px-1.5 py-1 text-center">{toDisplayRate(row.sgstRate).toFixed(2)}%</td>
                    <td className="border border-gray-300 px-1.5 py-1 text-right">{row.sgstAmount.toFixed(2)}</td>
                  </>
                )}
                {isGst && (
                  <>
                    <td className="border border-gray-300 px-1.5 py-1 text-center">{toDisplayRate(row.igstRate).toFixed(2)}%</td>
                    <td className="border border-gray-300 px-1.5 py-1 text-right">{row.igstAmount.toFixed(2)}</td>
                  </>
                )}
                <td className="border border-gray-300 px-1.5 py-1 text-right">{row.totalTaxAmount.toFixed(2)}</td>
              </tr>
            ))}
            <tr className="bg-gray-50 font-semibold">
              <td className="border border-gray-300 px-1.5 py-1">TOTAL</td>
              <td className="border border-gray-300 px-1.5 py-1">{calc.taxableAmount.toFixed(2)}</td>
              {!isGst && (
                <>
                  <td className="border border-gray-300 px-1.5 py-1" />
                  <td className="border border-gray-300 px-1.5 py-1 text-right">{calc.totalCgst.toFixed(2)}</td>
                  <td className="border border-gray-300 px-1.5 py-1" />
                  <td className="border border-gray-300 px-1.5 py-1 text-right">{calc.totalSgst.toFixed(2)}</td>
                </>
              )}
              {isGst && (
                <>
                  <td className="border border-gray-300 px-1.5 py-1" />
                  <td className="border border-gray-300 px-1.5 py-1 text-right">{calc.totalIgst.toFixed(2)}</td>
                </>
              )}
              <td className="border border-gray-300 px-1.5 py-1 text-right">{calc.totalTax.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="mt-3 border rounded px-2 py-1">
        <p className="text-xs text-gray-800 leading-snug">
          <strong>Tax Amount (in words):</strong> INR {amountInWords(calc.totalTax)}
        </p>
        <p className="text-xs font-semibold text-gray-900 mt-1.5">Company&apos;s PAN: {branchDetails?.panNo ?? 'NA'}</p>
      </div>
      <div className="mt-3 flex justify-between gap-4 border-t pt-3 items-start">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-gray-900">Declaration:</p>
          <p className="text-xs text-gray-700 leading-snug mt-0.5">
            We declare that this invoice shows the actual price of the goods described and that all particulars are true and
            correct.
          </p>
        </div>
        <div className="text-right shrink-0 min-w-[11rem]">
          {digitallySignedByName?.trim() ? (
            <div className="text-gray-900">
              {branchDisplayName ? (
                <p className="text-xs font-bold text-right mb-1.5">for {branchDisplayName}</p>
              ) : null}
              <div className="flex items-start justify-end gap-1.5">
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5 print:text-green-700" aria-hidden />
                <div className="text-right text-[10px] leading-tight text-gray-900">
                  <div className="font-normal">{digitallySignedByName.trim()}</div>
                </div>
              </div>
              <p className="text-xs font-bold text-right mt-2 border-b border-black pb-0.5">Authorised Signatory</p>
            </div>
          ) : (
            <p className="text-xs font-medium text-gray-900">Authorized Signatory</p>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="invoice-chunked-print-root invoice-chunked-print invoice-a4-stage">
      {Array.from({ length: copies }).map((_, copyIndex) => {
        const copyLabel =
          copyIndex === 0
            ? 'ORIGINAL FOR RECIPIENT'
            : copyIndex === 1
              ? 'DUPLICATE FOR TRANSPORTER'
              : copyIndex === 2
                ? 'TRIPLICATE FOR SUPPLIER'
                : 'EXTRA COPY';
        return (
          <React.Fragment key={copyIndex}>
            {chunks.map((chunk, chunkIndex) => {
              const isLastChunk = chunkIndex === chunks.length - 1;
              const globalStart = chunks.slice(0, chunkIndex).reduce((s, c) => s + c.length, 0);
              const currentPage = copyIndex * chunks.length + chunkIndex + 1;
              const nextPageNum = currentPage + 1;

              const breakAfter =
                !isLastChunk || copyIndex < copies - 1
                  ? 'always'
                  : showEwayBillOnView
                    ? 'always'
                    : 'auto';

              return (
                <div
                  key={`${copyIndex}-${chunkIndex}`}
                  className="invoice-a4-sheet invoice-chunk-page invoice-container bg-white"
                  style={{ pageBreakAfter: breakAfter as React.CSSProperties['pageBreakAfter'] }}
                >
                  {/* HEADER: full document header on every page (above line items) */}
                  <div className="invoice-doc-header invoice-print-header-block print:break-inside-avoid">
                    <div className="flex items-start justify-center gap-6 mt-2 mb-0 flex-wrap">
                      <h2 className="text-center font-bold text-xl">
                        Tax Invoice
                        {showPageNumberInTitle && (
                          <span className="font-bold"> (Page {currentPage})</span>
                        )}
                      </h2>
                      <div className="text-xs text-gray-600 font-semibold uppercase tracking-wide mt-1">
                        ({copyLabel})
                      </div>
                    </div>

                    <div className="mb-4 border border-gray-300 rounded-lg p-3 bg-gray-50">
                      <div className="flex items-stretch justify-between gap-4" style={{ minHeight: '2in' }}>
                        <div className="text-sm text-gray-800 flex flex-col justify-end">
                          <div className="grid grid-cols-[90px_1fr] gap-x-2 gap-y-1">
                            <div className="font-semibold">IRN</div>
                            <div className="break-all">{invoice.irn || '—'}</div>
                            <div className="font-semibold">Ack No.</div>
                            <div>{invoice.ackNo || '—'}</div>
                            <div className="font-semibold">Ack Date</div>
                            <div>{invoice.ackDate ? formatDate(invoice.ackDate) : '—'}</div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div className="text-xs text-gray-500 text-right">
                            <div className="font-semibold text-gray-700">e-Invoice</div>
                          </div>
                          {qrValue ? (
                            <div
                              className="bg-white border border-gray-300 rounded p-1"
                              style={{ width: '2in', height: '2in' }}
                            >
                              <QRCode value={qrValue} style={{ width: '100%', height: '100%' }} />
                            </div>
                          ) : (
                            <div className="text-xs text-gray-500">QR not available</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {renderAddressAndMeta()}
                  </div>

                  {/* BODY: line items only (+ continuation note); summary rows in table only on last body page */}
                  <div className="invoice-doc-body overflow-x-auto invoice-print-line-items">
                    <table className="w-full max-w-full border-collapse border border-gray-300 text-center text-[11px] leading-tight table-fixed">
                      {renderLineTableHead()}
                      <tbody>
                        {chunk.map((item, i) => (
                          <tr key={item.id}>
                            <td className="border border-gray-300 px-1 py-0.5">{globalStart + i + 1}</td>
                            <td className="border border-gray-300 px-1 py-0.5 text-left break-words w-[30%] min-w-0">
                              {item.endgoodName}
                            </td>
                            <td className="border border-gray-300 px-1 py-0.5">{item.boxes ??item.packingDetailIds?.length ?? '—'}</td>
                            <td className="border border-gray-300 px-1 py-0.5 break-all text-[10px]">
                              {item.userProvidedHeatNo || item.heatNo || '—'}
                            </td>
                            <td className="border border-gray-300 px-1 py-0.5 break-all">{item.hsn ?? '—'}</td>
                            <td className="border border-gray-300 px-1 py-0.5">{item.qty}</td>
                            <td className="border border-gray-300 px-1 py-0.5">{item.rate}</td>
                            <td className="border border-gray-300 px-1 py-0.5">{item.uom ?? 'Nos'}</td>
                            <td className="border border-gray-300 px-1 py-0.5">{(item.qty * item.rate).toFixed(2)}</td>
                          </tr>
                        ))}
                        {!isLastChunk && (
                          <tr className="bg-amber-50 text-[11px]">
                            <td colSpan={9} className="border border-gray-300 px-1 py-1 text-center font-semibold italic">
                              continued to page number {nextPageNum}
                            </td>
                          </tr>
                        )}
                        {isLastChunk && (
                          <>
                            <tr className="text-[11px]">
                              <td colSpan={5} className="border border-gray-300 px-1 py-0.5 text-right font-semibold" />
                              <td className="border border-gray-300 px-1 py-0.5" />
                              <td colSpan={2} className="border border-gray-300 px-1 py-0.5 text-right font-semibold" />
                              <td className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                                {calc.taxableAmount.toFixed(2)}
                              </td>
                            </tr>
                            {!isGst && (
                              <>
                                <tr className="text-[11px]">
                                  <td colSpan={4} className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                                    CGST
                                  </td>
                                  <td colSpan={4} className="border border-gray-300 px-1 py-0.5 text-right font-semibold" />
                                  <td className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                                    {calc.totalCgst.toFixed(2)}
                                  </td>
                                </tr>
                                <tr className="text-[11px]">
                                  <td colSpan={4} className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                                    SGST
                                  </td>
                                  <td colSpan={4} className="border border-gray-300 px-1 py-0.5 text-right font-semibold" />
                                  <td className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                                    {calc.totalSgst.toFixed(2)}
                                  </td>
                                </tr>
                              </>
                            )}
                            {isGst && (
                              <tr className="text-[11px]">
                                <td colSpan={4} className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                                  IGST
                                </td>
                                <td colSpan={4} className="border border-gray-300 px-1 py-0.5 text-right font-semibold" />
                                <td className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                                  {calc.totalIgst.toFixed(2)}
                                </td>
                              </tr>
                            )}
                            {otherChargesList.map((oc) => (
                              <tr key={oc.id} className="text-[11px]">
                                <td colSpan={4} className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                                  {oc.chargeName ?? oc.chargeMaster?.chargeName ?? ''} (
                                  {oc.chargeDescription ?? oc.chargeMaster?.description ?? ''})
                                </td>
                                <td colSpan={4} className="border border-gray-300 px-1 py-0.5" />
                                <td className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                                  {(oc.amount ?? 0).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                            <tr className="bg-gray-100 font-semibold text-[11px]">
                              <td colSpan={4} className="border border-gray-300 text-right py-1 px-1">
                                TOTAL
                              </td>
                              <td className="border border-gray-300 px-1 py-0.5" />
                              <td className="border border-gray-300 px-1 py-0.5 text-center">{calc.totalQty}</td>
                              <td colSpan={2} className="border border-gray-300 px-1 py-0.5" />
                              <td className="border border-gray-300 px-1 py-0.5 text-right">₹ {calc.totalAmount.toFixed(2)}</td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* FOOTER: everything below line items — only on last body page of this copy */}
                  {isLastChunk && (
                    <div className="invoice-doc-footer">
                      {renderTotalsAndAfter()}
                      {renderPrintFooter(currentPage)}
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        );
      })}

      {showEwayBillOnView && (
        <div
          className="invoice-a4-sheet invoice-eway-append print:break-before-page"
          style={{ pageBreakBefore: 'always' }}
        >
          <InvoiceEwayBillLayout invoice={invoice} branchDetails={branchDetails} shipToCustomer={shipToCustomer} />
        </div>
      )}
    </div>
  );
};
